import React, { useState, useEffect } from "react";
import {
  Container,
  Group,
  Select,
  Button,
  Divider,
  Stack,
  Paper,
  Switch,
  Checkbox,
  NumberInput,
  Grid,
  Box,
  ActionIcon,
  MultiSelect,
  SimpleGrid,
} from "@mantine/core";
import { IconUsers, IconPlus, IconTrash } from "@tabler/icons-react";
import BenchSettingsBreadcrumbs from "../../../UIComponent/Components/BenchSettingsBreadcrumbs";
import BenchTopHeader from "../../../UIComponent/Components/BenchTopHeader";
import BenchHeading from "../../../pages/UI/BenchHeading";

// Firebase & Redux Imports
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../../firebase/FirebaseCredential";
import { useAppSelector } from "../../../../hooks/hooks";
import { RootState } from "../../../../store/store";
import { toast } from "react-toastify";
import RewardModal from "../../../Rewards/RewardModal";

interface ReferralTier {
  id: string;
  referralCount: number;
  rewardType: string;
  rewardId?: string;
  rewardValue: number | "";
}

export default function ReferralSettings() {
  const companyId = useAppSelector(
    (state: RootState) => state.session.company_id,
  );
  const [loading, setLoading] = useState(false);
  const [rewardModalOpen, setRewardModalOpen] = useState(false);

  const [referralEnabled, setReferralEnabled] = useState(false);

  // Triggers with their specific rewards
  const [triggerConverted, setTriggerConverted] = useState({
    enabled: false,
    rewardType: "membership_extension",
    rewardId: "",
    rewardValue: "" as number | "",
  });
  const [triggerSpecificPackage, setTriggerSpecificPackage] = useState({
    enabled: false,
    rewardType: "discount_membership_package",
    rewardId: "",
    rewardValue: "" as number | "",
    packages: [] as string[],
  });

  const [rewardsList, setRewardsList] = useState<any[]>([]);
  const [packagesList, setPackagesList] = useState<
    { value: string; label: string }[]
  >([]);

  // Tiered Rewards
  const [rewardTiers, setRewardTiers] = useState<ReferralTier[]>([]);

  // Limits
  const [maxRewardsLimit, setMaxRewardsLimit] = useState<number | "">(50);

  // Rules
  const [allowInactive, setAllowInactive] = useState(false);
  const [inactiveDays, setInactiveDays] = useState<number | "">(60);

  const [allowEnquiryLost, setAllowEnquiryLost] = useState(false);
  const [allowEnquiryOpen, setAllowEnquiryOpen] = useState(false);
  const [enquiryOpenDays, setEnquiryOpenDays] = useState<number | "">(30);

  const fetchDependencies = async () => {
    if (!companyId) return;
    try {
      const rewardsQuery = query(
        collection(db, "companyprofile", companyId, "rewards"),
      );
      const rewardsSnap = await getDocs(rewardsQuery);
      setRewardsList(rewardsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const packagesQuery = query(
        collection(db, "packages"),
        where("company_id", "==", companyId),
      );
      const packagesSnap = await getDocs(packagesQuery);
      setPackagesList(
        packagesSnap.docs
          .filter((d) => d.data().type !== "trial")
          .map((d) => {
            const data = d.data();
            return {
              value: d.id,
              label:
                data.packageName ||
                data.name ||
                data.title ||
                "Unknown Package",
            };
          }),
      );
    } catch (error) {
      console.error("Error fetching dependencies:", error);
    }
  };

  useEffect(() => {
    const fetchSettingsAndRewards = async () => {
      if (!companyId) return;

      try {
        setLoading(true);

        // 1. Fetch Dependencies
        await fetchDependencies();

        // 2. Fetch Settings
        const companyProfileRef = collection(db, "companyprofile");
        const companyQuery = query(
          companyProfileRef,
          where("__name__", "==", String(companyId)),
        );
        const companySnapshot = await getDocs(companyQuery);

        if (companySnapshot.empty) return;

        const companyDocRef = companySnapshot.docs[0].ref;
        const referralSettingsRef = doc(
          companyDocRef,
          "companysettings",
          "referralsettings",
        );
        const docSnap = await getDoc(referralSettingsRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.referralEnabled !== undefined)
            setReferralEnabled(data.referralEnabled);
          if (data.triggers) {
            if (data.triggers.triggerConverted)
              setTriggerConverted(data.triggers.triggerConverted);
            if (data.triggers.triggerSpecificPackage)
              setTriggerSpecificPackage(data.triggers.triggerSpecificPackage);
          }
          if (data.rewardTiers) setRewardTiers(data.rewardTiers);
          if (data.maxRewardsLimit !== undefined)
            setMaxRewardsLimit(data.maxRewardsLimit);
          if (data.rules) {
            if (data.rules.allowInactive !== undefined)
              setAllowInactive(data.rules.allowInactive);
            if (data.rules.inactiveDays !== undefined)
              setInactiveDays(data.rules.inactiveDays);
            if (data.rules.allowEnquiryLost !== undefined)
              setAllowEnquiryLost(data.rules.allowEnquiryLost);
            if (data.rules.allowEnquiryOpen !== undefined)
              setAllowEnquiryOpen(data.rules.allowEnquiryOpen);
            if (data.rules.enquiryOpenDays !== undefined)
              setEnquiryOpenDays(data.rules.enquiryOpenDays);
          }
        }
      } catch (error) {
        console.error("Error fetching referral settings/rewards:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettingsAndRewards();
  }, [companyId]);

  const addTier = () => {
    setRewardTiers([
      ...rewardTiers,
      {
        id: Date.now().toString(),
        referralCount: (rewardTiers.length + 1) * 5,
        rewardType: "discount_membership_package",
        rewardValue: 10,
      },
    ]);
  };

  const removeTier = (id: string) => {
    setRewardTiers(rewardTiers.filter((tier) => tier.id !== id));
  };

  const updateTier = (id: string, field: keyof ReferralTier, value: any) => {
    setRewardTiers(
      rewardTiers.map((t) => (t.id === id ? { ...t, [field]: value } : t)),
    );
  };

  const updateTierFields = (id: string, updates: Partial<ReferralTier>) => {
    setRewardTiers(
      rewardTiers.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    );
  };

  const handleSave = async () => {
    if (!companyId) {
      toast.error("Company ID not found");
      return;
    }

    const referralSettingsData = {
      referralEnabled,
      triggers: { triggerConverted, triggerSpecificPackage },
      rewardTiers,
      maxRewardsLimit,
      rules: {
        allowInactive,
        inactiveDays,
        allowEnquiryLost,
        allowEnquiryOpen,
        enquiryOpenDays,
      },
    };

    try {
      setLoading(true);
      const companyProfileRef = collection(db, "companyprofile");
      const companyQuery = query(
        companyProfileRef,
        where("__name__", "==", String(companyId)),
      );
      const companySnapshot = await getDocs(companyQuery);

      if (companySnapshot.empty) {
        toast.error("Company not found.");
        setLoading(false);
        return;
      }

      const companyDocRef = companySnapshot.docs[0].ref;
      const referralSettingsRef = doc(
        companyDocRef,
        "companysettings",
        "referralsettings",
      );

      await setDoc(referralSettingsRef, referralSettingsData, {
        merge: true,
      });

      toast.success("Referral Settings saved successfully!");
    } catch (error) {
      console.error("Error saving referral settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    console.log("Cancelled");
  };

  const rewardCategoryOptions = [
    { value: "membership_extension", label: "Extend Membership" },
    { value: "free_classes", label: "Free Class Booking" },
    { value: "free_product", label: "Free Product" },
    {
      value: "discount_membership_package",
      label: "Discount on Membership Package",
    },
    { value: "discount_class_package", label: "Discount on Class Package" },
  ];

  const renderRewardFields = (
    triggerData: any,
    setTriggerData: (data: any) => void,
  ) => {
    const isTemplateReward = [
      "membership_extension",
      "free_classes",
      "free_product",
      "discount_membership_package",
      "discount_class_package",
    ].includes(triggerData.rewardType);

    return (
      <Grid align="flex-end" ml="xl">
        <Grid.Col span={12} sm={isTemplateReward ? 6 : 6}>
          <Select
            label="Reward Category"
            data={rewardCategoryOptions}
            value={triggerData.rewardType}
            onChange={(val) =>
              setTriggerData({
                ...triggerData,
                rewardType: val || "discount_membership_package",
                rewardId: "", // reset when category changes
              })
            }
          />
        </Grid.Col>

        {isTemplateReward ? (
          <Grid.Col span={12} sm={6}>
            <Select
              label="Select Reward"
              placeholder="Choose from existing rewards"
              data={[
                ...rewardsList
                  .filter((r) => r.reward_category === triggerData.rewardType)
                  .map((r) => ({ value: r.id, label: r.name })),
                { value: "add_new_reward", label: "➕ Add New Reward" },
              ]}
              value={
                triggerData.rewardId === "add_new_reward"
                  ? null
                  : triggerData.rewardId
              }
              onChange={(val) => {
                if (val === "add_new_reward") {
                  setRewardModalOpen(true);
                } else {
                  setTriggerData({
                    ...triggerData,
                    rewardId: val || "",
                  });
                }
              }}
              searchable
            />
          </Grid.Col>
        ) : (
          <Grid.Col span={12} sm={6}>
            <NumberInput
              label="Discount (%)"
              min={1}
              max={100}
              value={triggerData.rewardValue as number}
              onChange={(val) =>
                setTriggerData({
                  ...triggerData,
                  rewardValue: val,
                })
              }
            />
          </Grid.Col>
        )}
      </Grid>
    );
  };

  return (
    <Container size="lg">
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 110,
          zIndex: 100,
          backgroundColor: "#FAFAFA",
          paddingTop: theme.spacing.sm,
          marginTop: `-${theme.spacing.sm}`,
        })}
      >
        <SimpleGrid cols={2}>
          <BenchSettingsBreadcrumbs
            links={[{ label: "Referral Program Settings" }]}
          />
          <Group position="right">
            <Button onClick={handleCancel} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleSave} loading={loading}>
              Save
            </Button>
          </Group>
        </SimpleGrid>
        <Divider my="sm" />
      </Box>

      <BenchTopHeader
        icon={<IconUsers />}
        title="Referral Program Settings"
        description="Configure referral triggers, tiered rewards, and program rules to grow your member base."
      />

      <Paper p="lg" my="xl" withBorder>
        <Group position="apart">
          <BenchHeading
            title="Enable Referral Program"
            subheading="Toggle this on to allow members to refer others and earn rewards."
            order={4}
            fw={600}
            fz="h1"
          />
          <Switch
            checked={referralEnabled}
            onChange={(event) =>
              setReferralEnabled(event.currentTarget.checked)
            }
            size="lg"
            onLabel="ON"
            offLabel="OFF"
          />
        </Group>
      </Paper>

      <div
        style={{
          opacity: referralEnabled ? 1 : 0.5,
          pointerEvents: referralEnabled ? "auto" : "none",
        }}
      >
        {/* REWARD TRIGGERS */}
        <Paper p="lg" my="xl" withBorder>
          <Box mb="lg">
            <BenchHeading
              title="Reward Triggers"
              subheading="Select the scenarios when a referrer should receive a reward, and specify what they get."
              order={4}
              fw={600}
              fz="h1"
            />
          </Box>
          <Stack spacing="md">
            <Paper
              withBorder
              p="md"
              radius="md"
              bg={triggerConverted.enabled ? "brand.0" : "transparent"}
            >
              <Checkbox
                label={<b>When Member is Converted (First Payment Made)</b>}
                checked={triggerConverted.enabled}
                onChange={(e) =>
                  setTriggerConverted({
                    ...triggerConverted,
                    enabled: e.currentTarget.checked,
                  })
                }
                mb={triggerConverted.enabled ? "md" : 0}
                size="md"
              />
              {triggerConverted.enabled &&
                renderRewardFields(triggerConverted, setTriggerConverted)}
            </Paper>

            <Paper
              withBorder
              p="md"
              radius="md"
              bg={triggerSpecificPackage.enabled ? "brand.0" : "transparent"}
            >
              <Checkbox
                label={<b>When buying a specific package while converting</b>}
                checked={triggerSpecificPackage.enabled}
                onChange={(e) =>
                  setTriggerSpecificPackage({
                    ...triggerSpecificPackage,
                    enabled: e.currentTarget.checked,
                  })
                }
                mb={triggerSpecificPackage.enabled ? "md" : 0}
                size="md"
              />
              {triggerSpecificPackage.enabled && (
                <Stack ml="xl" spacing="sm">
                  <MultiSelect
                    label="Select Target Packages"
                    placeholder="Choose one or more packages"
                    data={packagesList}
                    value={triggerSpecificPackage.packages}
                    onChange={(val) =>
                      setTriggerSpecificPackage({
                        ...triggerSpecificPackage,
                        packages: val,
                      })
                    }
                  />
                  {renderRewardFields(
                    triggerSpecificPackage,
                    setTriggerSpecificPackage,
                  )}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Paper>

        {/* TIERED REWARDS */}
        <Paper p="lg" my="xl" withBorder>
          <Group position="apart" mb="lg">
            <Box>
              <BenchHeading
                title="Number of Referrals Based Rewards"
                subheading="Set up bonus milestone rewards (e.g., 5th referral gets Free PT)."
                order={4}
                fw={600}
                fz="h1"
              />
            </Box>
            <Button
              variant="light"
              size="sm"
              leftIcon={<IconPlus size={14} />}
              onClick={addTier}
            >
              Add Tier
            </Button>
          </Group>

          <Stack spacing="md">
            {rewardTiers.map((tier, index) => (
              <Paper key={tier.id} withBorder p="md" radius="md" bg="gray.0">
                <Grid align="flex-end">
                  <Grid.Col span={12} sm={3}>
                    <NumberInput
                      label="Referral Count"
                      description="E.g. On their Nth referral"
                      value={tier.referralCount}
                      onChange={(val) =>
                        updateTier(tier.id, "referralCount", val)
                      }
                      min={1}
                    />
                  </Grid.Col>
                  <Grid.Col span={12} sm={4}>
                    <Select
                      label="Reward Category"
                      value={tier.rewardType}
                      onChange={(val) => {
                        updateTierFields(tier.id, {
                          rewardType: val || "",
                          rewardId: "",
                        });
                      }}
                      data={rewardCategoryOptions}
                    />
                  </Grid.Col>
                  {[
                    "membership_extension",
                    "free_classes",
                    "free_product",
                  ].includes(tier.rewardType) ? (
                    <Grid.Col span={12} sm={4}>
                      <Select
                        label="Select Reward"
                        placeholder="Choose"
                        data={[
                          ...rewardsList
                            .filter(
                              (r) => r.reward_category === tier.rewardType,
                            )
                            .map((r) => ({ value: r.id, label: r.name })),
                          {
                            value: "add_new_reward",
                            label: "➕ Add New Reward",
                          },
                        ]}
                        value={
                          tier.rewardId === "add_new_reward"
                            ? null
                            : tier.rewardId
                        }
                        onChange={(val) => {
                          if (val === "add_new_reward") {
                            setRewardModalOpen(true);
                          } else {
                            updateTier(tier.id, "rewardId", val || "");
                          }
                        }}
                        searchable
                      />
                    </Grid.Col>
                  ) : (
                    <Grid.Col span={12} sm={4}>
                      <NumberInput
                        label="Discount (%)"
                        value={tier.rewardValue as number}
                        onChange={(val) =>
                          updateTier(tier.id, "rewardValue", val)
                        }
                        min={1}
                        max={100}
                      />
                    </Grid.Col>
                  )}
                  <Grid.Col span={12} sm={1}>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      mb={4}
                      onClick={() => removeTier(tier.id)}
                      disabled={rewardTiers.length === 0}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Grid.Col>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </Paper>

        {/* LIMITS AND RULES */}
        <Paper p="lg" my="xl" withBorder>
          <Box mb="lg">
            <BenchHeading
              title="Limits & Rules"
              subheading="Set reward limits and configure validation logic."
              order={4}
              fw={600}
              fz="h1"
            />
          </Box>

          <Grid mb="xl">
            <Grid.Col span={12} sm={6}>
              <NumberInput
                label="Overall Limit for Rewards"
                description="Maximum total rewards a single member can earn from referrals"
                value={maxRewardsLimit}
                onChange={setMaxRewardsLimit}
                min={1}
              />
            </Grid.Col>
          </Grid>

          <Divider mb="xl" />

          <Stack spacing="lg">
            <Group align="flex-end">
              <Checkbox
                label="Allow to refer an inactive member"
                checked={allowInactive}
                onChange={(e) => setAllowInactive(e.currentTarget.checked)}
                size="md"
              />
              {allowInactive && (
                <NumberInput
                  placeholder="Days"
                  label="Inactive for at least (days)"
                  value={inactiveDays}
                  onChange={setInactiveDays}
                  min={1}
                  size="xs"
                  style={{ width: 150 }}
                />
              )}
            </Group>

            <Checkbox
              label="Allow if the phone number exists in Enquiry but was marked as 'Converted' or 'Lost'"
              checked={allowEnquiryLost}
              onChange={(e) => setAllowEnquiryLost(e.currentTarget.checked)}
              size="md"
            />

            <Group align="flex-end">
              <Checkbox
                label="Allow if Enquiry is 'Open', but the enquiry was created before"
                checked={allowEnquiryOpen}
                onChange={(e) => setAllowEnquiryOpen(e.currentTarget.checked)}
                size="md"
              />
              {allowEnquiryOpen && (
                <NumberInput
                  placeholder="Days"
                  label="Older than (days)"
                  value={enquiryOpenDays}
                  onChange={setEnquiryOpenDays}
                  min={1}
                  size="xs"
                  style={{ width: 150 }}
                />
              )}
            </Group>
          </Stack>
        </Paper>
      </div>
      <RewardModal
        opened={rewardModalOpen}
        onClose={() => setRewardModalOpen(false)}
        onSaveSuccess={fetchDependencies}
      />
    </Container>
  );
}
